import { makeAutoObservable, observable, runInAction } from 'mobx';
import axios from 'axios';
import urls from './Urls';
import { FALLBACK_INWORLD_MODELS, FALLBACK_MODELS, VOICE_INFO, describeError, sortVoices } from './AudioCatalog';

/**
 * The settings of the automatic creation of the audio files (ai_settings.audio.speech on the server).
 *
 * The answer of GET /api/ai/settings/audio:
 *  - configured   what is saved in the database for Google (may hold a value that the server does not support)
 *  - effective    what the automatic creation really uses with Google (always valid; effective.fellBack and
 *                 effective.warnings tell when a saved value was replaced)
 *  - catalog      the models, the voices, the languages and the limits of Google that the server supports
 *  - capabilities what the server can do (ffmpeg, Gemini API key, Inworld API key)
 *  - inworld      { configured, effective, catalog } - the same three parts for Inworld (SCRUM-1847)
 *  - auto         { configured, effective, catalog } - the rules of the automatic creation per dictionary and language
 * A server that is older than this screen sends neither inworld nor auto.
 */
class AudioSettingsStore {
    view = null;
    loading = false;
    /** How many times the settings were loaded from the server (not counting the answers of the saves) - see useServerForm */
    loadCount = 0;
    saving = false;
    /** { status, message, detail } of the last load that failed, or null */
    loadError = null;
    /** { status, message, detail } of the last save that failed, or null */
    saveError = null;
    /** the time of the last successful save (ms), 0 - none */
    savedAt = 0;

    // The same three values for the other two parts that are saved here: they are separate, because the
    // screens of all the parts stay mounted and one of them must not show the error of another.
    savingInworld = false;
    saveInworldError = null;
    savedInworldAt = 0;
    savingAuto = false;
    saveAutoError = null;
    savedAutoAt = 0;

    /** The latest failures of the automatic creation: an array (the newest first), or null until they are loaded */
    failures = null;
    failuresLoading = false;
    failuresError = null;

    constructor() {
        makeAutoObservable(this, { failures: observable.shallow });
    }

    get catalog() {
        return this.view?.catalog || null;
    }

    get effective() {
        return this.view?.effective || null;
    }

    get capabilities() {
        return this.view?.capabilities || null;
    }

    /** The Inworld part of the view, or null (a server that is older than this screen) */
    get inworld() {
        return this.view?.inworld?.effective && this.view?.inworld?.catalog ? this.view.inworld : null;
    }

    /** The rules part of the view, or null (a server that is older than this screen) */
    get auto() {
        return this.view?.auto?.effective && this.view?.auto?.catalog ? this.view.auto : null;
    }

    get models() {
        return this.catalog?.models?.length ? this.catalog.models : FALLBACK_MODELS;
    }

    get inworldModels() {
        return this.inworld?.catalog?.models?.length ? this.inworld.catalog.models : FALLBACK_INWORLD_MODELS;
    }

    /** The models of both providers (the Gemini models first) */
    get allModels() {
        return [...this.models, ...this.inworldModels];
    }

    get voices() {
        return sortVoices(this.catalog?.voices?.length ? this.catalog.voices : Object.keys(VOICE_INFO));
    }

    load = async () => {
        if (this.loading) return;
        this.loading = true;
        this.loadError = null;
        try {
            const res = await axios.get(urls.ai.audioSettings, { withCredentials: true });
            if (!res.data?.effective || !res.data?.catalog) {
                throw new Error('Unexpected answer of the server');
            }
            runInAction(() => {
                this.view = res.data;
                this.loadCount += 1;
            });
        } catch (err) {
            runInAction(() => {
                this.loadError = describeError(err);
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    /**
     * Sends a PATCH and takes the whole new view from the answer.
     * @returns {Promise<boolean>} true - saved
     */
    patchView = async (url, patch, setState) => {
        setState({ saving: true, error: null });
        try {
            const res = await axios.patch(url, patch, { withCredentials: true });
            if (!res.data?.effective || !res.data?.catalog) {
                throw new Error('Unexpected answer of the server');
            }
            runInAction(() => {
                this.view = res.data;
            });
            setState({ saving: false, savedAt: Date.now() });
            return true;
        } catch (err) {
            setState({ saving: false, error: describeError(err) });
            return false;
        }
    };

    /**
     * Saves part of the settings of Google: { model, prompt, he|en_US|en_GB|ar: { male, female } }.
     * The server answers with the new settings.
     * @returns {Promise<boolean>} true - saved
     */
    save = (patch) => this.patchView(urls.ai.audioSettings, patch, (state) => runInAction(() => {
        if ('saving' in state) this.saving = state.saving;
        if ('error' in state) this.saveError = state.error;
        if ('savedAt' in state) this.savedAt = state.savedAt;
    }));

    /**
     * Saves part of the settings of Inworld: { model, speakingRate, deliveryMode, he|en_US|en_GB|ar: { male, female }, he: { niqqud } }.
     * @returns {Promise<boolean>} true - saved
     */
    saveInworld = (patch) => this.patchView(urls.ai.audioSettingsInworld, patch, (state) => runInAction(() => {
        if ('saving' in state) this.savingInworld = state.saving;
        if ('error' in state) this.saveInworldError = state.error;
        if ('savedAt' in state) this.savedInworldAt = state.savedAt;
    }));

    /**
     * Saves the rules of the automatic creation: { enabled, rules: { heb_to_eng|eng_to_heb: { he|en_US|en_GB|ar: { enabled, provider } } } }.
     * @returns {Promise<boolean>} true - saved
     */
    saveAuto = (patch) => this.patchView(urls.ai.audioSettingsAuto, patch, (state) => runInAction(() => {
        if ('saving' in state) this.savingAuto = state.saving;
        if ('error' in state) this.saveAutoError = state.error;
        if ('savedAt' in state) this.savedAutoAt = state.savedAt;
    }));

    clearSaveError = () => {
        this.saveError = null;
    };

    /** The latest failures of the automatic creation (GET /api/ai/settings/audio/failures), the newest first */
    loadFailures = async (limit = 50) => {
        if (this.failuresLoading) return;
        this.failuresLoading = true;
        this.failuresError = null;
        try {
            const res = await axios.get(urls.ai.audioFailures, { params: { limit }, withCredentials: true });
            if (!Array.isArray(res.data?.failures)) {
                throw new Error('Unexpected answer of the server');
            }
            runInAction(() => {
                this.failures = res.data.failures;
            });
        } catch (err) {
            runInAction(() => {
                this.failuresError = describeError(err);
            });
        } finally {
            runInAction(() => {
                this.failuresLoading = false;
            });
        }
    };
}

const audioSettingsStore = new AudioSettingsStore();
export default audioSettingsStore;
